import * as z from 'zod';

import { emailsQuerySchema } from '@/_containers/inputs/emails/shared/schema';
import { auditQuerySchema } from '@/_containers/view-logs/schemas/audit-query-schema';
import { contactQueryLinkerSchema } from '@/_pages/contacts/shared/schemas/contact-query-linker-schema';
import { invoiceQueryLinkerSchema } from '@/_pages/invoices/shared/schemas/invoices/invoice-query-linker-schema';
import { brandQueryLinkerSchema } from '@/_pages/settings/brands/shared';
import { emailTemplateQueryLinkerSchema } from '@/_pages/settings/email-templates/shared/schemas/email-templates-query-linker-schema';
import { systemUserQueryLinkerSchema } from '@/_pages/system-users/shared/schemas/system-user-query-linker-schema';
import { companyQueryLinkerSchema } from '@/_schemas/companies/company-query-linker-schema';
import { paginatedResponseSchema } from '@/_schemas/generic/paginated-response';

import { deliveryDetailsSchema, emailMetadataSchema } from './email-history-json-schema';
import { emailHistoryQueryLinkerSchema } from './email-history-query-linker-schema';

export const emailHistorySourceTypeSchema = z.enum(['invoice']);

export type EmailHistorySourceTypeSchema = z.infer<typeof emailHistorySourceTypeSchema>;

const emailHistoryEmailTemplateSchema = emailTemplateQueryLinkerSchema.extend({
  email_subject: z.string().optional(),
  email_body: z.string().nullable().optional(),
  email_type: z.string().optional(),
});

/** Recipient contacts on email history may include emails for matching `email_metadata.to`. */
export const emailHistoryRecipientContactSchema = contactQueryLinkerSchema.extend({
  emails: emailsQuerySchema.nullish(),
});

export type EmailHistoryRecipientContactSchema = z.infer<typeof emailHistoryRecipientContactSchema>;

export const emailHistoryQuerySchema = z.object({
  ...emailHistoryQueryLinkerSchema.shape,

  source_type: emailHistorySourceTypeSchema,
  source_id: z.number(),
  email_template_id: z.number(),
  brand_id: z.number().nullable(),
  client_id: z.number().nullable(),
  sender_id: z.number().nullable(),
  email_body: z.string().nullable(),
  email_metadata: z.union([z.string(), emailMetadataSchema]).nullable(),
  delivery_details: z.union([z.string(), deliveryDetailsSchema]).nullable(),
  delivery_status: z.string(),
  internet_message_id: z.string().nullish(),
  scheduled_at: z.string().nullable().optional(),
  sent_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),

  source: invoiceQueryLinkerSchema.nullable(),
  email_template: emailHistoryEmailTemplateSchema.nullable(),
  brand: brandQueryLinkerSchema.nullable(),
  client: companyQueryLinkerSchema.nullable(),
  sender: systemUserQueryLinkerSchema.nullable(),
  email_recipient_contacts: z.array(emailHistoryRecipientContactSchema).nullish(),
  audits: z.array(auditQuerySchema).nullish(),
});

export type EmailHistoryQuerySchema = z.infer<typeof emailHistoryQuerySchema>;

export const emailHistoryQueryAllSchema = paginatedResponseSchema(emailHistoryQuerySchema);
export type EmailHistoryQueryAllSchema = z.infer<typeof emailHistoryQueryAllSchema>;
